import type { ExpressionSpecification } from 'mapbox-gl';
import type { TrailResult } from '../redux/reducer';

const tileTrailCountExpression: ExpressionSpecification = ['coalesce', ['get', 'trail_count'], 0];
const noTrailheadsExpression: ExpressionSpecification = ['literal', false];

export type TrailheadListState = {
  isTrailCompleted?: (trailId: number) => boolean;
  isTrailSaved?: (trailId: number) => boolean;
};

export type TrailheadResultExpressions = {
  /** True for the trailheads where every trail is closed. */
  isClosedExpression: ExpressionSpecification;
  /** True for the trailheads where every trail is completed. */
  isCompletedExpression: ExpressionSpecification;
  /** True for the trailheads where at least one trail is saved. */
  isSavedExpression: ExpressionSpecification;
  /** Draws only the trailheads with at least one trail in the search results. */
  matchedTrailheadsFilter: ExpressionSpecification;
  popularityExpression: ExpressionSpecification | number;
  trailCountExpression: ExpressionSpecification;
};

const trailheadIdsExpression = (trailheadIds: number[]): ExpressionSpecification =>
  trailheadIds.length ? ['in', ['id'], ['literal', trailheadIds]] : noTrailheadsExpression;

const isTrailheadId = (trailheadId: unknown): trailheadId is number =>
  typeof trailheadId === 'number' && Number.isSafeInteger(trailheadId) && trailheadId > 0;

/**
 * Groups the trails explore currently has by the trailhead ids on each hit. A trail is drawn at its
 * primary trailhead and at each secondary one. Mapbox can't read the search results, so pin filters,
 * counts, and popularity are resolved here and matched per trailhead id.
 *
 * `matchedTrailheadsFilter` names the trailheads to draw. A tile that loads later stays hidden until
 * one of its trails is in the results, instead of flashing a pin the search would have dropped.
 */
export function trailsByTrailheadId(trailResults: Record<string, TrailResult[]> | undefined): Map<number, TrailResult[]> {
  const byTrailheadId = new Map<number, TrailResult[]>();
  if (!trailResults) {
    return byTrailheadId;
  }

  const seenTrailIds = new Map<number, Set<number>>();
  Object.values(trailResults).forEach(trails => {
    trails.forEach(trail => {
      const trailheadIds = [trail.primary_trailhead_id, ...(trail.secondary_trailhead_ids ?? [])];
      trailheadIds.forEach(trailheadId => {
        if (!isTrailheadId(trailheadId)) {
          return;
        }
        const seen = seenTrailIds.get(trailheadId) ?? new Set<number>();
        if (seen.has(trail.ID)) {
          return;
        }
        seen.add(trail.ID);
        seenTrailIds.set(trailheadId, seen);
        const grouped = byTrailheadId.get(trailheadId);
        if (grouped) {
          grouped.push(trail);
        } else {
          byTrailheadId.set(trailheadId, [trail]);
        }
      });
    });
  });

  return byTrailheadId;
}

/**
 * The saved and completed states live in the user's lists rather than on the trail, which is why
 * they come in as callbacks and leave as id lists the pin layers can test with `in`.
 */
export function buildTrailheadResultExpressions(
  trailResults: Record<string, TrailResult[]> | undefined,
  { isTrailCompleted, isTrailSaved }: TrailheadListState = {}
): TrailheadResultExpressions {
  const trailsByTrailhead = trailsByTrailheadId(trailResults);
  if (!trailsByTrailhead.size) {
    return {
      isClosedExpression: noTrailheadsExpression,
      isCompletedExpression: noTrailheadsExpression,
      isSavedExpression: noTrailheadsExpression,
      matchedTrailheadsFilter: noTrailheadsExpression,
      popularityExpression: 0,
      trailCountExpression: tileTrailCountExpression
    };
  }

  const popularityExpression: ExpressionSpecification = ['match', ['id']];
  const trailCountExpression: ExpressionSpecification = ['match', ['id']];
  const matchedTrailheadIds: number[] = [];
  const closedTrailheadIds: number[] = [];
  const completedTrailheadIds: number[] = [];
  const savedTrailheadIds: number[] = [];

  [...trailsByTrailhead.keys()]
    .sort((a, b) => a - b)
    .forEach(id => {
      const trails = trailsByTrailhead.get(id) ?? [];
      matchedTrailheadIds.push(id);
      trailCountExpression.push(id, trails.length);

      if (trails.every(trail => trail.is_closed)) {
        closedTrailheadIds.push(id);
      }
      // A trailhead stands for several trails, so one saved trail is enough to badge it, the same way
      // the grouped trail pins treat a partially saved group.
      if (isTrailSaved && trails.some(trail => isTrailSaved(Number(trail.ID)))) {
        savedTrailheadIds.push(id);
      }
      if (isTrailCompleted && trails.every(trail => isTrailCompleted(Number(trail.ID)))) {
        completedTrailheadIds.push(id);
      }

      const popularities = trails.map(trail => trail.popularity).filter((popularity): popularity is number => typeof popularity === 'number');
      if (popularities.length) {
        popularityExpression.push(id, popularities.reduce((total, popularity) => total + popularity, 0) / popularities.length);
      }
    });

  const hasPopularity = popularityExpression.length > 2;
  const hasTrailCounts = trailCountExpression.length > 2;
  popularityExpression.push(0);
  trailCountExpression.push(tileTrailCountExpression);

  return {
    isClosedExpression: trailheadIdsExpression(closedTrailheadIds),
    isCompletedExpression: trailheadIdsExpression(completedTrailheadIds),
    isSavedExpression: trailheadIdsExpression(savedTrailheadIds),
    matchedTrailheadsFilter: trailheadIdsExpression(matchedTrailheadIds),
    popularityExpression: hasPopularity ? popularityExpression : 0,
    trailCountExpression: hasTrailCounts ? trailCountExpression : tileTrailCountExpression
  };
}
